import { useEffect, useState } from "react";
import { MemberCheckinEntry } from "../../../model/members/MemberCheckinEntry";
import dayjs from "dayjs";
import { Card, Skeleton, Table, Statistic, Row, Col } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { useMemberService } from "../../../services/members/MemberServiceProvider";
import type { SortOrder } from 'antd/es/table/interface';

interface MemberCheckinsViewProps {
    membershipId: string;
    backgroundColor?: string;
    borderColor?: string;
}

const MemberCheckinsView: React.FC<MemberCheckinsViewProps> = ({ 
    membershipId,
    backgroundColor = '#ffffff',
    borderColor = '#e5e7eb'
}) => {
    const [loading, setLoading] = useState(true);
    const [checkins, setCheckins] = useState<MemberCheckinEntry[]>([]);
    const memberService = useMemberService();

    useEffect(() => {
        if (membershipId) {
            const fetchCheckins = async () => {
                try {
                    const history = await memberService.getCheckinHistory(membershipId);
                    setCheckins(history);
                } catch (error) {
                    console.error("Error fetching checkin history:", error);
                    setCheckins([]);
                } finally {
                    setLoading(false);
                }
            };
            fetchCheckins();
        }
    }, [membershipId, memberService]);

    if (loading) {
        return <Skeleton active />;
    }

    // Get the last check-in time (sorted by most recent)
    const sortedCheckins = [...checkins].sort((a, b) => 
        new Date(b.checkInUtc).getTime() - new Date(a.checkInUtc).getTime()
    );
    const lastCheckin = sortedCheckins.length > 0 ? sortedCheckins[0] : null;

    // Calculate check-ins in the last 30 days
    const thirtyDaysAgo = dayjs().subtract(30, 'days');
    const checkinsLast30Days = checkins.filter(checkin => 
        dayjs(checkin.checkInUtc).isAfter(thirtyDaysAgo)
    ).length;

    // Format date to browser's local timezone
    const formatCheckinTime = (date: Date) => {
        // dayjs will automatically convert UTC dates to local timezone when formatting
        return dayjs(date).format('ddd DD MMM YYYY HH:mm:ss');
    };

    const formatLastCheckinDisplay = (date: Date) => {
        const checkinDayjs = dayjs(date);
        const now = dayjs();
        const diffDays = now.diff(checkinDayjs, 'day');
        
        if (diffDays === 0) {
            return `Today at ${checkinDayjs.format('HH:mm')}`;
        } else if (diffDays === 1) {
            return `Yesterday at ${checkinDayjs.format('HH:mm')}`;
        } else if (diffDays < 7) {
            return `${diffDays} days ago (${checkinDayjs.format('ddd DD MMM YYYY HH:mm')})`;
        } else {
            return checkinDayjs.format('ddd DD MMM YYYY HH:mm');
        }
    };

    const columns = [
        {
            title: 'Check-in Time',
            dataIndex: 'checkInUtc',
            key: 'checkInUtc',
            render: (date: Date) => formatCheckinTime(date),
            sorter: (a: MemberCheckinEntry, b: MemberCheckinEntry) => 
                new Date(a.checkInUtc).getTime() - new Date(b.checkInUtc).getTime(),
            defaultSortOrder: 'descend' as SortOrder
        }
    ];

    return (
        <Card 
            title="Member Check-ins"
            className="mb-4"
            style={{ 
                backgroundColor,
                borderColor
            }}
        >
            {/* Summary Statistics */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#f5f5f5' }}>
                        <Statistic 
                            title="Last Check-in"
                            value={lastCheckin ? formatLastCheckinDisplay(lastCheckin.checkInUtc) : 'Never'}
                            prefix={lastCheckin ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : null}
                            valueStyle={{ 
                                color: lastCheckin ? '#52c41a' : '#8c8c8c',
                                fontSize: '16px'
                            }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#f5f5f5' }}>
                        <Statistic
                            title="Check-ins (Last 30 Days)"
                            value={checkinsLast30Days}
                            valueStyle={{ fontSize: '24px', color: '#1890ff' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Check-ins Table */}
            <Table
                columns={columns}
                dataSource={checkins}
                pagination={{ pageSize: 10 }}
                rowKey={(record, index) => `${record.checkInUtc}-${index}`}
                locale={{
                    emptyText: 'No check-ins found'
                }}
            />
        </Card>
    );
};

export default MemberCheckinsView;
